import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAllItems, useProjects, useToggleItem, useAllSubtasks, subtaskCounts } from "@/hooks/useData";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { isOverdue, isToday, isTomorrow, thisWeekRange, nextWeekRange, parseDate, startOfDay, scheduledFor } from "@/lib/format";
import { useIsMobile } from "@/lib/useMediaQuery";
import { itemsById as buildItemsById } from "@/lib/deps";
import { listNesting, progressCounts } from "@/lib/hierarchy";
import {
  STATUS_ORDER, STATUS_META, PRIORITY_META, TYPE_META,
  type Item, type ItemType, type Priority, type Project,
} from "@/lib/types";
import { ItemRow, itemProjectId } from "@/components/ItemRow";
import { card, GroupHeading, groupTone } from "@/components/atoms";
import { useChrome, useSearchQuery, matchesQuery } from "@/context/ChromeContext";
import { RegisterBadges, RegisterPanel, type RegisterKey } from "@/components/Registers";
import { CalendarView } from "@/views/CalendarView";
import { HeaderTabs } from "@/components/HeaderTabs";

type Preset = "today" | "week" | "next" | "overdue" | "all" | "upcoming" | "calendar";
type Scope = Preset;
type Group = "due" | "week" | "project" | "status" | "priority" | "type";

const PRIORITY_WEIGHT: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
const SCOPE_LABEL: Record<Scope, string> = { today: "Today", week: "This week", next: "Next week", overdue: "Overdue", all: "All items", upcoming: "Upcoming", calendar: "Calendar" };

/**
 * Work's five scopes. Dashboard, Today, Upcoming and Calendar were four
 * separate destinations; they are one screen with header tabs now.
 *
 * "This week" and "Next week" have no chip — Upcoming covers both and splits
 * them, so they were the same thing twice. Their routes still work and keep a
 * tab when you arrive on one directly, so the row never reads as
 * unselected from a bookmark.
 */
function SCOPE_TABS(scope: Scope): [Scope, string][] {
  const base: [Scope, string][] = [
    ["today", "Today"], ["upcoming", "Upcoming"], ["overdue", "Overdue"],
    ["calendar", "Calendar"], ["all", "All items"],
  ];
  if (scope === "week" || scope === "next") {
    return [base[0], base[1], [scope, SCOPE_LABEL[scope]], base[2], base[3], base[4]];
  }
  return base;
}

function sortItems(items: Item[]): Item[] {
  return [...items].sort((a, b) => {
    const da = scheduledFor(a) || "9999-99-99", db = scheduledFor(b) || "9999-99-99";
    if (da !== db) return da.localeCompare(db);
    return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
  });
}

export function ListView({ preset }: { preset: Preset }) {
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const toggle = useToggleItem();
  const { data: subs = [] } = useAllSubtasks();
  const { openItem } = useTaskEditor();
  const mobile = useIsMobile();
  const query = useSearchQuery();
  const nav = useNavigate();

  // Scope is driven by the route (/today, /week, /next, /all). All four routes
  // render this same ListView instance, so a local useState(preset) would stick
  // on its first value when you navigate between them from the sidebar. Deriving
  // scope from the preset prop — and having the tabs navigate — keeps the URL
  // the single source of truth so both entry points stay in sync.
  const scope: Scope = preset;
  const [group, setGroup] = useState<Group>(preset === "upcoming" ? "week" : "due");
  const [client, setClient] = useState("");
  // The register side column. Kept across scopes — every Tasks route shares
  // this instance — so it stays open while you move between lists.
  const [panel, setPanel] = useState<RegisterKey | null>(null);

  const projectById = new Map<string, Project>(projects.map((p) => [p.id, p]));
  const byId = buildItemsById(items);
  const subCounts = progressCounts(items, subtaskCounts(subs));

  // Distinct client names across real projects (the Inbox has none), for the filter.
  const clients = Array.from(
    new Set(projects.filter((p) => p.kind !== "inbox" && p.client).map((p) => p.client))
  ).sort((a, b) => a.localeCompare(b));

  const [twStart, twEnd] = thisWeekRange();
  const [nwStart, nwEnd] = nextWeekRange();
  const today = startOfDay();

  const clientOf = (i: Item): string => projectById.get(itemProjectId(i) || "")?.client || "";
  const byClient = (i: Item): boolean => !client || clientOf(i) === client;

  const searched = items.filter((i) => matchesQuery(i.title, query) && byClient(i));
  const open = searched.filter((i) => i.status !== "done");

  const inScope = (i: Item): boolean => {
    if (scope === "all") return true;
    if (scope === "overdue") return isOverdue(i);
    if (scope === "today") return isOverdue(i) || isToday(scheduledFor(i));
    if (scope === "week") {
      if (isOverdue(i)) return true;
      const d = parseDate(scheduledFor(i));
      return !!d && d >= (today > twStart ? today : twStart) && d < twEnd;
    }
    if (scope === "upcoming") {
      const d = parseDate(scheduledFor(i));
      return !!d && d >= (today > twStart ? today : twStart) && d < nwEnd;
    }
    const d = parseDate(scheduledFor(i));
    return !!d && d >= nwStart && d < nwEnd;
  };
  const scoped = open.filter(inScope);

  // --- Build the groups for the current grouping dimension (handoff §4b) ----
  let groups: { title: string; count: number; items: Item[]; warn?: boolean; tone?: string }[] = [];

  if (group === "week") {
    // Today and tomorrow lead, then the rest of each week. Tomorrow wins over
    // both weeks, so on a Sunday it is not filed under next week.
    const dayOf = (i: Item) => scheduledFor(i);
    const dueToday = sortItems(scoped.filter((i) => isToday(dayOf(i))));
    const dueTomorrow = sortItems(scoped.filter((i) => isTomorrow(dayOf(i))));
    const thisWeek = sortItems(scoped.filter((i) => {
      if (isToday(dayOf(i)) || isTomorrow(dayOf(i))) return false;
      const d = parseDate(dayOf(i));
      return !!d && d < twEnd;
    }));
    const nextWeek = sortItems(scoped.filter((i) => {
      if (isTomorrow(dayOf(i))) return false;
      const d = parseDate(dayOf(i));
      return !!d && d >= nwStart && d < nwEnd;
    }));
    groups = [
      { title: "Today", count: dueToday.length, items: dueToday },
      { title: "Tomorrow", count: dueTomorrow.length, items: dueTomorrow },
      { title: "This week", count: thisWeek.length, items: thisWeek },
      { title: "Next week", count: nextWeek.length, items: nextWeek, tone: "var(--muted)" },
    ];
  } else if (group === "type") {
    groups = (Object.keys(TYPE_META) as ItemType[]).map((t) => {
      const list = sortItems(scoped.filter((i) => i.type === t));
      return { title: TYPE_META[t].label, count: list.length, items: list };
    });
  } else if (group === "due") {
    const overdue = sortItems(scoped.filter(isOverdue));
    const dueToday = sortItems(scoped.filter((i) => isToday(scheduledFor(i)) && !isOverdue(i)));
    const dueTomorrow = sortItems(scoped.filter((i) => isTomorrow(scheduledFor(i))));
    const thisWeek = sortItems(scoped.filter((i) => {
      if (isOverdue(i) || isToday(scheduledFor(i)) || isTomorrow(scheduledFor(i))) return false;
      const d = parseDate(scheduledFor(i));
      return d && d >= twStart && d < twEnd;
    }));
    const later = sortItems(scoped.filter((i) => {
      if (isOverdue(i) || isToday(scheduledFor(i)) || isTomorrow(scheduledFor(i))) return false;
      const d = parseDate(scheduledFor(i));
      return !d || d >= twEnd;
    }));
    groups = [
      { title: "Overdue", count: overdue.length, items: overdue, warn: true },
      { title: "Today", count: dueToday.length, items: dueToday },
      { title: "Tomorrow", count: dueTomorrow.length, items: dueTomorrow },
      { title: "This week", count: thisWeek.length, items: thisWeek },
      { title: "Later", count: later.length, items: later },
    ];
    // Completed group only appears in the unfiltered scope.
    if (scope === "all") {
      const completed = searched.filter((i) => i.status === "done");
      groups.push({ title: "Completed", count: completed.length, items: completed.slice(0, 50) });
    }
  } else if (group === "project") {
    groups = projects.map((p) => {
      const list = sortItems(scoped.filter((i) => itemProjectId(i) === p.id));
      return { title: p.name, count: list.length, items: list };
    });
  } else if (group === "status") {
    groups = STATUS_ORDER.filter((s) => s !== "done").map((s) => {
      const list = sortItems(scoped.filter((i) => i.status === s));
      return { title: STATUS_META[s].label, count: list.length, items: list, warn: s === "blocked" };
    });
  } else {
    groups = (["urgent", "high", "medium", "low"] as Priority[]).map((p) => {
      const list = sortItems(scoped.filter((i) => i.priority === p));
      return { title: PRIORITY_META[p].label, count: list.length, items: list, warn: p === "urgent" };
    });
  }
  groups = groups.filter((g) => g.items.length > 0);

  // The Dashboard's three stat cards fold into this one line, freeing the first
  // screen for rows rather than three numbers in boxes.
  const overdueCount = open.filter((i) => isOverdue(i)).length;
  const todayCount = open.filter((i) => isToday(scheduledFor(i)) && !isOverdue(i)).length;
  const subtitle = [
    scope === "calendar" ? null : `${scoped.length} item${scoped.length === 1 ? "" : "s"}`,
    client || null,
    // "date", not "due date": these lists group by scheduledFor — the day you
    // plan to work on it, falling back to a deadline when that is all there is.
    // Calling that "due date" is what made the deadline and the date look
    // interchangeable in the first place.
    scope === "calendar" ? null : `grouped by ${group === "due" ? "date" : group}`,
    overdueCount ? `${overdueCount} overdue` : null,
    todayCount ? `${todayCount} due today` : null,
    `${open.length} open`,
  ].filter(Boolean).join(" · ");

  useChrome(
    {
      title: "Tasks", subtitle,
      tabs: <HeaderTabs value={scope} options={SCOPE_TABS(scope)} onChange={(v) => nav(`/${v}`)} />,
      actions: mobile || scope === "calendar" ? undefined : (
        <RegisterBadges open={open} byId={byId} active={panel}
          onToggle={(k) => setPanel((p) => (p === k ? null : k))} />
      ),
    },
    [scope, group, scoped.length, client, overdueCount, todayCount, open.length, panel]
  );

  const showPanel = !!panel && !mobile && scope !== "calendar";

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1320 }}>
      {/* Controls: client filter + grouping. The scopes are the header tabs;
          the calendar has neither control, so it gets no row. */}
      {scope !== "calendar" && (
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        {clients.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--muted)" }}>Client</span>
            <div style={{ position: "relative" }}>
              <select value={client} onChange={(e) => setClient(e.target.value)} style={clientSelect}>
                <option value="">All clients</option>
                {clients.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", color: "var(--muted2)", pointerEvents: "none", fontSize: 10 }}>▾</span>
            </div>
          </label>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: mobile ? 0 : "auto" }}>
          <span style={{ fontSize: 11.5, color: "var(--muted)" }}>Group</span>
          <Segmented
            value={group}
            onChange={(v) => setGroup(v as Group)}
            options={[
              ...(scope === "upcoming" ? [["week", "Week"] as [string, string]] : [["due", "Due"] as [string, string]]),
              ["project", "Project"], ["status", "Status"], ["priority", "Priority"], ["type", "Type"],
            ]}
          />
        </div>
      </div>
      )}

      {/* The registers open from the header badges as a 340px column; closed,
          the list has the full width. The calendar never gets the column — a
          month grid beside it leaves neither enough room. */}
      <div className="work-shell">
      <div className="work-grid" data-panel={showPanel || undefined} style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr)",
        gap: 18, alignItems: "start",
      }}>
        <div>
          {scope === "calendar" ? (
            <CalendarView />
          ) : groups.length === 0 ? (
            <div style={{ ...card, padding: "22px 18px", fontSize: 13, color: "var(--muted2)" }}>Nothing here. Enjoy the breathing room.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {groups.map((g) => {
                // Todoist subtasks ride inside their parent when both are in this group.
                const nest = listNesting(g.items, items);
                return (
                  <div key={g.title}>
                    <GroupHeading label={g.title} count={nest.top.length} tone={g.tone ?? groupTone(g.title, g.warn)} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {nest.top.map((it) => (
                        <ItemRow key={it.id} item={it} projectById={projectById} onToggle={toggle} onOpen={openItem} itemsById={byId} subCounts={subCounts}
                          subtasks={nest.childrenOf(it)} childrenOf={nest.childrenOf} parent={nest.parentOf(it)} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showPanel && (
          <div className="work-panel">
            <RegisterPanel open={open} byId={byId} projectById={projectById} active={panel}
              onSelect={setPanel} onClose={() => setPanel(null)} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div style={{ display: "flex", gap: 0, padding: 3, background: "var(--tint)", borderRadius: 999, overflowX: "auto" }}>
      {options.map(([v, label]) => {
        const on = v === value;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: "6px 12px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none", whiteSpace: "nowrap",
            background: on ? "var(--surface)" : "transparent", color: on ? "var(--accent)" : "var(--ink3)", fontWeight: on ? 500 : 400,
            boxShadow: on ? "var(--shadowCard)" : "none",
          }}>{label}</button>
        );
      })}
    </div>
  );
}

const pillLink: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999,
  border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
  fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap",
};

const clientSelect: React.CSSProperties = {
  appearance: "none", WebkitAppearance: "none", padding: "7px 26px 7px 11px", borderRadius: 999,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
  fontSize: 12.5, cursor: "pointer", maxWidth: 180,
};
